import React, { useRef, useState, useEffect } from 'react';
import { useEditorState } from '@tiptap/react';
import {
  Bold, Italic, Underline, Strikethrough, Highlighter,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Image as ImageIcon,
  Heading1, Heading2,
  Undo, Redo, RemoveFormatting, Palette,
  ChevronDown, Sigma
} from 'lucide-react';

// دکمه‌های تولبار — بیرون از بدنه‌ی رندر تعریف شده‌اند تا در هر رندر بازسازی نشوند
const ToolbarButton = ({ onClick, isActive, icon: Icon, title, disabled = false, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`h-8 px-2 rounded-md transition-all flex items-center justify-center text-xs gap-1 font-medium select-none ${isActive
      ? 'bg-blue-600 text-white shadow-xs'
      : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 active:bg-slate-200'
      } ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
  >
    {Icon && <Icon size={15} />}
    {children}
  </button>
);

const Divider = () => <div className="w-[1px] h-5 bg-slate-200 mx-1 self-center" />;

const COLORS = ['#000000', '#2563eb', '#dc2626', '#16a34a', '#d97706', '#9333ea', '#475569'];

const HIGHLIGHT_COLORS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa', '#e2e8f0'];

const BASIC_OPERATORS = [
  { label: '+', symbol: ' + ', title: 'جمع (+)' },
  { label: '−', symbol: ' − ', title: 'تفریق (−)' },
  { label: '×', symbol: ' × ', title: 'ضرب (×)' },
  { label: '÷', symbol: ' ÷ ', title: 'تقسیم (÷)' },
  { label: '=', symbol: ' = ', title: 'مساوی (=)' },
  { label: '≠', symbol: ' ≠ ', title: 'نامساوی (≠)' },
  { label: '±', symbol: ' ± ', title: 'مثبت و منفی (±)' },
];

// ساختارهای فرمول و نمادهای تکمیلی
const MORE_SYMBOLS = [
  {
    category: 'ساختارهای فرمول',
    symbols: [
      { label: '( )', text: '( ) ', title: 'پرانتز' },
      { label: '[ ]', text: '[ ] ', title: 'کروشه' },
      { label: '{ }', text: '{ } ', title: 'آکولاد' },
      { label: '|x|', text: '|x| ', title: 'قدر مطلق' },
      { label: '<', text: ' < ', title: 'کوچک‌تر' },
      { label: '>', text: ' > ', title: 'بزرگ‌تر' },
      { label: '≤', text: ' ≤ ', title: 'کوچک‌تر یا مساوی' },
      { label: '≥', text: ' ≥ ', title: 'بزرگ‌تر یا مساوی' },
      { label: '≈', text: ' ≈ ', title: 'تقریباً برابر' },
    ]
  },
  {
    category: 'نمادها',
    symbols: [
      { label: '%', text: ' % ', title: 'درصد' },
      { label: '°', text: '° ', title: 'درجه' },
      { label: 'π', text: ' π ', title: 'عدد پی' },
      { label: '∠', text: ' ∠ ', title: 'زاویه' },
      { label: '△', text: ' △ ', title: 'مثلث' },
      { label: '⊥', text: ' ⊥ ', title: 'عمود' },
      { label: '∥', text: ' ∥ ', title: 'موازی' },
    ]
  },
];

const EditorToolbar = ({ editor }) => {
  const fileInputRef = useRef(null);
  const colorPickerRef = useRef(null);
  const highlightPickerRef = useRef(null);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showSymbolsPicker, setShowSymbolsPicker] = useState(false);

  // بستن پنل‌ها با کلیک بیرون
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
      if (highlightPickerRef.current && !highlightPickerRef.current.contains(event.target)) {
        setShowHighlightPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ریرندر تولبار در هر تراکنش ادیتور — بدون این، وضعیت دکمه‌ها (فعال/غیرفعال)
  // فقط بعد از تایپ کاراکتر جدید به‌روز می‌شود و انتخاب مارک معلوم نمی‌شود
  useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return null;
      return {
        bold: e.isActive('bold'),
        italic: e.isActive('italic'),
        underline: e.isActive('underline'),
        strike: e.isActive('strike'),
        h1: e.isActive('heading', { level: 1 }),
        h2: e.isActive('heading', { level: 2 }),
        bulletList: e.isActive('bulletList'),
        orderedList: e.isActive('orderedList'),
        textAlignRight: e.isActive({ textAlign: 'right' }),
        textAlignCenter: e.isActive({ textAlign: 'center' }),
        textAlignLeft: e.isActive({ textAlign: 'left' }),
        highlight: e.isActive('highlight'),
        canUndo: e.can().undo(),
        canRedo: e.can().redo(),
        color: e.getAttributes('textStyle').color,
        highlightColor: e.getAttributes('highlight').color,
      };
    },
  });

  if (!editor) {
    return null;
  }

  // رنگ فعلی متن انتخاب‌شده (از خود ادیتور) — برای نشانگر روی دکمه
  const currentColor = editor.getAttributes('textStyle').color;

/**
 * فشرده‌سازی و بهینه‌سازی خودکار تصویر در کلاینت
 * برای جلوگیری قطعی از خطای 413 (Payload Too Large) سرور
 */
const compressImageFile = (file, maxDim = 1200, quality = 0.82) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.onerror = () => resolve(e.target.result);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
};

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    // فشرده‌سازی هوشمند و کم‌حجم‌سازی در کلاینت قبل از درج در ادیتور
    const compressedSrc = await compressImageFile(file);
    if (compressedSrc) {
      editor.chain().focus().setImage({ src: compressedSrc, align: 'center', width: '50%' }).run();
    }
  };

  const closeAllPickers = () => {
    setShowColorPicker(false);
    setShowHighlightPicker(false);
    setShowSymbolsPicker(false);
  };

  return (
    <div className="bg-slate-50/90 border-b border-slate-200 rounded-t-xl select-none" dir="rtl">
      {/* ═══ ردیف ۱: ابزارهای متن ═══ */}
      <div className="p-1.5 pb-0 flex flex-wrap items-center gap-1">
        {/* تاریخچه */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          icon={Undo}
          title="بازگردانی (Ctrl+Z)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          icon={Redo}
          title="تکرار (Ctrl+Y)"
        />

        <Divider />

        {/* سرتیترها */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          icon={Heading1}
          title="سرتیتر بزرگ"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          icon={Heading2}
          title="سرتیتر متوسط"
        />

        <Divider />

        {/* قالب‌بندی متن */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          icon={Bold}
          title="ضخیم (Ctrl+B)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          icon={Italic}
          title="مورب (Ctrl+I)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          icon={Underline}
          title="زیرخط (Ctrl+U)"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          icon={Strikethrough}
          title="خط‌خورده"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          icon={RemoveFormatting}
          title="حذف فرمت"
        />

        <Divider />

        {/* رنگ و هایلایت */}
        <div className="relative" ref={colorPickerRef}>
          <button
            type="button"
            onClick={() => { setShowColorPicker(!showColorPicker); setShowHighlightPicker(false); }}
            title={`رنگ متن${currentColor ? ' — رنگ فعلی' : ''}`}
            style={{ backgroundColor: currentColor ? `${currentColor}1f` : undefined, color: currentColor || '#475569' }}
            className={`h-8 px-2 rounded-md transition-colors flex items-center ${currentColor ? '' : 'text-slate-600 hover:bg-slate-200/70'}`}
          >
            <Palette size={15} />
          </button>
          {showColorPicker && (
            <div className="absolute top-full right-0 mt-1 p-2 bg-white border border-slate-200 rounded-xl shadow-xl z-30 flex gap-1.5">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setColor(c).run();
                    setShowColorPicker(false);
                  }}
                  className="w-5 h-5 rounded-full border border-slate-300 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="relative" ref={highlightPickerRef}>
          <button
            type="button"
            onClick={() => { setShowHighlightPicker(!showHighlightPicker); setShowColorPicker(false); }}
            title="هایلایت"
            style={{ backgroundColor: editor.isActive('highlight') ? `${editor.getAttributes('highlight').color || '#fef08a'}80` : undefined }}
            className={`h-8 px-2 rounded-md transition-colors flex items-center ${
              editor.isActive('highlight') ? 'text-slate-900' : 'text-slate-600 hover:bg-slate-200/70'
            }`}
          >
            <Highlighter size={15} />
          </button>
          {showHighlightPicker && (
            <div className="absolute top-full right-0 mt-1 p-2 bg-white border border-slate-200 rounded-xl shadow-xl z-30 flex items-center gap-1.5">
              {HIGHLIGHT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setHighlight({ color: c }).run();
                    setShowHighlightPicker(false);
                  }}
                  className="w-5 h-5 rounded-md border border-slate-300 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                />
              ))}
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().unsetHighlight().run();
                  setShowHighlightPicker(false);
                }}
                className="px-1.5 py-0.5 text-xs text-rose-600 hover:bg-rose-50 rounded"
              >
                حذف
              </button>
            </div>
          )}
        </div>

        <Divider />

        {/* ترازبندی */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          icon={AlignRight}
          title="راست‌چین"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          icon={AlignCenter}
          title="وسط‌چین"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          icon={AlignLeft}
          title="چپ‌چین"
        />

        <Divider />

        {/* لیست‌ها */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          icon={List}
          title="لیست نشانه‌دار"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          icon={ListOrdered}
          title="لیست شماره‌دار"
        />

        <Divider />

        {/* تصویر و جدول */}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          ref={fileInputRef}
          onChange={handleImageUpload}
        />
        <ToolbarButton
          onClick={() => fileInputRef.current?.click()}
          icon={ImageIcon}
          title="افزودن تصویر — بلافاصله درج می‌شود و آپلود در پس‌زمینه انجام می‌گیرد"
        >
          <span className="text-xs">تصویر</span>
        </ToolbarButton>
      </div>

      {/* ═══ ردیف ۲: ابزارهای ریاضی (دوره ابتدایی) ═══ */}
      <div className="p-1.5 pt-1 flex flex-wrap items-center gap-1 bg-blue-50/40 border-t border-slate-200/70 rounded-b-xl">
        {/* برچسب بخش ریاضی */}
        <span className="flex items-center gap-1 px-1.5 text-[11px] font-bold text-blue-700/80 select-none">
          <Sigma size={13} />
          فرمول
        </span>

        <Divider />

        {/* ساختارهای اصلی — درج مستقیم داخل ادیتور؛ جاهای خالی همان‌جا تایپ می‌شوند (مثل Equation ورد) */}
        <ToolbarButton onClick={() => editor.chain().focus().insertFraction().run()} title="کسر — درج و تایپ مستقیم در ادیتور">
          <span className="font-medium text-xs">کسر</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().insertMixedFraction().run()} title="عدد مخلوط — درج و تایپ مستقیم در ادیتور">
          <span className="font-medium text-xs">مخلوط</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().insertPower().run()} title="توان — درج و تایپ مستقیم در ادیتور">
          <span className="font-bold text-xs" style={{ direction: 'ltr' }}>x²</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().insertSubscript().run()} title="اندیس — درج و تایپ مستقیم در ادیتور">
          <span className="font-bold text-xs" style={{ direction: 'ltr' }}>x₂</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().insertSqrt().run()} title="رادیکال — درج و تایپ مستقیم در ادیتور">
          <span className="font-bold text-xs" style={{ direction: 'ltr' }}>√x</span>
        </ToolbarButton>

        <Divider />

        {/* اپراتورهای پایه */}
        {BASIC_OPERATORS.map((op) => (
          <ToolbarButton
            key={op.label}
            onClick={() => editor.chain().focus().insertContent(op.symbol).run()}
            title={op.title}
          >
            <span className="font-bold text-sm leading-none">{op.label}</span>
          </ToolbarButton>
        ))}

        {/* دراپ‌داوان ساختارها و نمادهای بیشتر */}
        <div className="relative mr-auto">
          <ToolbarButton
            onClick={() => setShowSymbolsPicker(!showSymbolsPicker)}
            isActive={showSymbolsPicker}
            title="سایر ساختارها و نمادهای ریاضی"
          >
            <span className="text-xs">بیشتر</span>
            <ChevronDown size={12} className={`transition-transform ${showSymbolsPicker ? 'rotate-180' : ''}`} />
          </ToolbarButton>

          {showSymbolsPicker && (
            <div className="absolute top-full left-0 mt-1.5 p-2.5 bg-white border border-slate-200 rounded-xl shadow-xl z-40 w-72 text-slate-800" onMouseLeave={() => setShowSymbolsPicker(false)}>
              {MORE_SYMBOLS.map((group, gIdx) => (
                <div key={group.category} className={gIdx > 0 ? 'mt-2 pt-2 border-t border-slate-100' : ''}>
                  <div className="text-[10px] font-semibold text-slate-400 mb-1.5 px-0.5">{group.category}</div>
                  <div className="grid grid-cols-5 gap-1">
                    {group.symbols.map((s) => (
                      <button
                        key={s.label}
                        type="button"
                        onClick={() => {
                          editor.chain().focus().insertContent(s.text || s.label + ' ').run();
                          closeAllPickers();
                        }}
                        title={s.title}
                        className="h-8 rounded-md text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-blue-50 hover:text-blue-600 border border-slate-200/80 transition-colors flex items-center justify-center"
                        style={{ direction: 'ltr' }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditorToolbar;
